import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { authApi, api } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * My Account — self-service profile + password management.
 *
 * Two stacked sections:
 *   1. Profile — name, role-specific display names, phone. Identity fields
 *      (email, city) are shown read-only. Customer affiliation, role flags,
 *      and area position are sysadmin-only and not surfaced here.
 *   2. Password — change own password. Requires current password.
 *
 * After a successful password change, must_change_password clears server-side.
 * The user object in local storage refreshes so the rest of the app sees the
 * new state immediately.
 */
export default function Account() {
    const { user, refreshUser } = useAuth()

    if (!user) {
        return <div className="p-8 text-center text-gray-500">Loading...</div>
    }

    return (
        <div className="space-y-6 max-w-2xl">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">My Account</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Edit your profile and change your password. Account-level settings
                    (email, customer, role flags) are managed by the literature chair.
                </p>
            </div>

            <ProfileSection user={user} refreshUser={refreshUser} />
            <PasswordSection />
        </div>
    )
}

// ============================================================================
//  Profile section
// ============================================================================
function ProfileSection({ user, refreshUser }) {
    const hasCustomer = !!user.customerId
    const isGsr = user.isGsr === true
    const isAreaAdmin = user.isAreaAdmin === true

    const [form, setForm] = useState({
        name: user.name || '',
        litRepName: user.litRepName || '',
        gsrName: user.gsrName || '',
        areaAdminName: user.areaAdminName || '',
        phone: user.phone || '',
    })
    const [busy, setBusy] = useState(false)

    function update(field, value) {
        setForm(f => ({ ...f, [field]: value }))
    }

    async function handleSubmit(e) {
        e.preventDefault()
        if (!form.name.trim()) {
            toast.error('Name is required')
            return
        }

        setBusy(true)
        try {
            const payload = {
                name: form.name.trim(),
                litRepName: hasCustomer && form.litRepName.trim() ? form.litRepName.trim() : null,
                gsrName: isGsr && form.gsrName.trim() ? form.gsrName.trim() : null,
                areaAdminName: isAreaAdmin && form.areaAdminName.trim() ? form.areaAdminName.trim() : null,
                phone: form.phone.trim() || null,
            }
            const response = await api.put('/api/users/me', payload)
            if (response.success) {
                toast.success('Profile updated')
                // Refresh the cached user so name changes etc. show up in nav, dashboard, forms
                if (refreshUser) await refreshUser()
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-ghasc-navy">Profile</h3>
            </div>

            <div className="p-6 space-y-4">
                {/* Read-only identity fields */}
                <div className="grid sm:grid-cols-2 gap-3">
                    <ReadOnlyField label="Email" value={user.email} hint="Contact the chair to change." />
                    <ReadOnlyField label="City" value={user.city || '—'} hint="Set by the chair." />
                </div>
                {user.customerName && (
                    <ReadOnlyField label="Customer / Homegroup" value={user.customerName} hint="Set by the chair." />
                )}

                {/* Editable name */}
                <Input
                    id="acc-name"
                    label="Account Name"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    required
                />

                {/* Editable phone */}
                <Input
                    id="acc-phone"
                    label="Phone (optional)"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                />

                {/* Role-specific names */}
                {(hasCustomer || isGsr || isAreaAdmin) && (
                    <div className="border-t border-gray-100 pt-4 space-y-3">
                        <p className="text-sm font-medium text-gray-700">Role names</p>

                        {hasCustomer && (
                            <Input
                                id="acc-lit-rep-name"
                                label="Lit Rep Name (used on orders)"
                                placeholder={`Falls back to "${form.name || 'account name'}" if blank`}
                                value={form.litRepName}
                                onChange={(e) => update('litRepName', e.target.value)}
                            />
                        )}

                        {isGsr && (
                            <Input
                                id="acc-gsr-name"
                                label="GSR Name (used on GSR reports)"
                                placeholder={`Falls back to "${form.name || 'account name'}" if blank`}
                                value={form.gsrName}
                                onChange={(e) => update('gsrName', e.target.value)}
                            />
                        )}

                        {isAreaAdmin && (
                            <>
                                <Input
                                    id="acc-area-admin-name"
                                    label="Area Admin Name (used on area reports)"
                                    placeholder={`Falls back to "${form.name || 'account name'}" if blank`}
                                    value={form.areaAdminName}
                                    onChange={(e) => update('areaAdminName', e.target.value)}
                                />
                                <ReadOnlyField label="Area Position" value={user.areaPosition || '—'} hint="Set by the chair." />
                            </>
                        )}
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    <Button type="submit" loading={busy}>Save Profile</Button>
                </div>
            </div>
        </form>
    )
}

// ============================================================================
//  Password section
// ============================================================================
function PasswordSection() {
    const { refreshUser } = useAuth()
    const [current, setCurrent] = useState('')
    const [pw1, setPw1] = useState('')
    const [pw2, setPw2] = useState('')
    const [busy, setBusy] = useState(false)

    async function handleSubmit(e) {
        e.preventDefault()
        if (!current) {
            toast.error('Please enter your current password')
            return
        }
        if (pw1.length < 6) {
            toast.error('New password must be at least 6 characters')
            return
        }
        if (pw1 !== pw2) {
            toast.error("New passwords don't match")
            return
        }

        setBusy(true)
        try {
            const response = await api.post('/api/users/me/change-password', {
                currentPassword: current,
                newPassword: pw1,
            })
            if (response.success) {
                toast.success('Password changed')
                setCurrent('')
                setPw1('')
                setPw2('')
                // Refresh — clears must_change_password in the cached user
                if (refreshUser) await refreshUser()
            } else {
                toast.error(response.message || 'Password change failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-ghasc-navy">Change Password</h3>
            </div>

            <div className="p-6 space-y-4">
                <Input
                    id="acc-current-pw"
                    label="Current Password"
                    type="password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    autoComplete="current-password"
                    required
                />
                <Input
                    id="acc-new-pw"
                    label="New Password"
                    type="password"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    autoComplete="new-password"
                    required
                />
                <Input
                    id="acc-confirm-pw"
                    label="Confirm New Password"
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    required
                />

                <div className="flex justify-end pt-2">
                    <Button type="submit" loading={busy}>Change Password</Button>
                </div>
            </div>
        </form>
    )
}

// ============================================================================
//  Helpers
// ============================================================================
function ReadOnlyField({ label, value, hint }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                {value}
            </div>
            {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        </div>
    )
}